import { useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const PortalSwitcher = ({ currentPortal = 'hrms' }) => {
  const [switching, setSwitching] = useState(false);

  const portals = [
    {
      id: 'hrms',
      name: 'HRMS',
      icon: '/favicon.ico',
      url: process.env.REACT_APP_HRMS_URL || process.env.REACT_APP_BASE_URL?.replace(':5000', ':3000') || 'http://localhost:3000',
      active: currentPortal === 'hrms'
    },
    {
      id: 'onboarding',
      name: 'Onboarding',
      icon: '/favicon.ico',
      url:
        process.env.REACT_APP_ONBOARDING_URL ||
        process.env.REACT_APP_REDIRECT_TO_ONBOARDING ||
        'http://localhost:5173',
      active: currentPortal === 'onboarding'
    }
  ];

  const currentPortalData = portals.find(p => p.active) || portals[0];

  const handlePortalSwitch = async (portal) => {
    if (portal.active) return;

    setSwitching(true);
    
    try {
      // Get current auth token to pass to other portal
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Construct URL with token for seamless authentication
      const targetUrl = new URL(portal.url);
      if (token) {
        targetUrl.searchParams.set('auth_token', token);
        targetUrl.searchParams.set('portal_switch', 'true');
      }
      
      // Switch to the other portal in the same tab
      window.location.href = targetUrl.toString();
    } catch (error) {
      console.error('Error switching portals:', error);
      // Fallback: open in new tab if same-tab switch fails
      window.open(portal.url, '_blank');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button className="flex items-center w-full text-left hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200 group">
            <div className={`p-2 mr-3 flex-shrink-0`}>
              <img 
                src={currentPortalData.icon} 
                alt={`${currentPortalData.name} icon`}
                className="w-6 h-6" 
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-gray-900 truncate">
                {currentPortalData.name}
              </h1>
            </div>
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-all duration-200 flex-shrink-0 ml-2 ${
              open ? 'rotate-180' : 'rotate-0'
            }`} />
          </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items 
          className="absolute left-0 top-full mt-2 w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
        >
          <div className="p-1">
            {portals.map((portal) => {
              return (
                <Menu.Item key={portal.id}>
                  {({ active }) => (
                    <button
                      onClick={() => handlePortalSwitch(portal)}
                      disabled={portal.active || switching}
                      className={`${
                        active && !portal.active ? 'bg-gray-50' : ''
                      } ${
                        portal.active ? 'bg-blue-50' : ''
                      } flex items-center w-full px-3 py-2 text-left rounded-md transition-colors duration-200 ${
                        portal.active ? 'cursor-default' : 'cursor-pointer'
                      } ${switching ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="p-1 mr-2 flex-shrink-0">
                        <img 
                          src={portal.icon} 
                          alt={`${portal.name} icon`}
                          className="w-4 h-4" 
                        />
                      </div>
                      <span className={`text-sm font-medium ${
                        portal.active ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {portal.name}
                      </span>
                      {portal.active && (
                        <span className="ml-auto text-xs text-blue-600">•</span>
                      )}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>
          
          {switching && (
            <div className="px-3 py-2 border-t border-gray-100">
              <div className="flex items-center text-xs text-gray-600">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
                Switching...
              </div>
            </div>
          )}
        </Menu.Items>
      </Transition>
        </>
      )}
    </Menu>
  );
};

export default PortalSwitcher;